import { useState } from 'react';
import { GRADE_META, type Grade } from '@core/sim/cosmetic/cosmeticCatalog';
import { CSBadge, CSModal } from './cs';
import { CastAvatar, CAST } from './cast';
import { botCastId } from '@core/community/communityMeta';
import { CosmeticCard } from './CosmeticCard';
import { PRISM_GRADIENT } from '../sim/cosmeticTheme';
import { metalOf, type Metal } from '../../domain/core/economy/tiers';

export type SocialCosmetic = {
  slug?: string | null;
  grade?: Grade | null;
  level?: number | null;
  /** 유저 레벨(user_levels.level) — 가챠 강화 `level`과 별개. 있으면 아바타 우하단 메탈 도트를 그린다(2026-09-03 채택 ①+③ 혼합안). null = 도트 없음(익명·봇·구버전 응답). */
  userLevel?: number | null;
  /** 캐릭터를 아직 안 고른 유저(장착 행 0 → 황둥이 폴백). 접근성 라벨에만 쓴다 — 채도 저하는 2026-09-12 폐기. */
  unset?: boolean;
  cardId?: string | null;
  cardKey?: string | null;
  profileFrame?: string | null;
  titleKey?: string | null;
};

export function cosmeticFromAuthor(row: any): SocialCosmetic {
  const cosmetic = row?.author_cosmetic || {};
  return {
    slug: cosmetic.slug || row?.author_avatar_char || 'hwangdoongi',
    grade: cosmetic.grade || row?.author_avatar_grade || null,
    level: cosmetic.enhance_level ?? row?.author_avatar_level ?? 0,
    // attachLevels가 author_id 있는 행에만 author_level(기본 1)을 붙인다 → 익명 댓글은 undefined → 도트 없음
    userLevel: row?.author_level != null ? Number(row.author_level) || 1 : null,
    // attachLevels 폴백 객체엔 card_key가 없다(공개 API·뷰 행은 base:… 또는 <grade>:… 로 항상 있음) → 미선택 판정
    unset: !cosmetic.card_key && !row?.author_avatar_grade,
    cardId: cosmetic.card_id || null,
    cardKey: cosmetic.card_key || null,
    profileFrame: cosmetic.profile_frame || null,
    titleKey: cosmetic.title_key || null,
  };
}

/**
 * AI 봇 작성자 아바타 — 커뮤니티 피드·게시글 상세·댓글 공통 (2026-09-09 신설).
 *
 * 🔴 **왜 만들었나**: 종전엔 세 화면이 각자 `<CastAvatar ... ring ringColor=… style={{borderRadius}}/>`를
 * 직접 불렀는데, `CastAvatar`의 `bare` 기본값이 `true`라 **ring이 아예 그려지지 않았다**
 * (cast.jsx "ring/ringColor/halo는 bare 모드에서 무시됨 — 데드 prop"). 즉 2026-09-03에 의도한
 * "봇=둥근사각 / 유저=원형" 실루엣 구분이 실제 화면에는 없었고, 투명 PNG에 `borderRadius`만 걸려
 * 유저 아바타와 구분되지 않았다. 댓글에는 분기 자체가 없었다.
 *
 * **구분 수단 = 우하단 AI 도트** (효신님 2026-09-09). 첫 구현은 봇을 둥근사각으로 만들고 링을 둘렀는데
 * 두 가지가 틀렸다: ① `surface-2` 오라 + padding은 `border` 속성만 아닐 뿐 **눈에는 2px 테두리**다(§G-2 위반) —
 * 유저 쪽 링은 등급을 나타내는 기능이 있지만 봇 쪽은 아무 의미 없는 장식이었다.
 * ② 캐릭터 얼굴은 원형이 자연스러운데 봇만 사각이라 라인업에서 이질적이었다.
 *
 * 그래서 **아바타는 원형으로 통일**하고, 유저에게 레벨 도트가 붙는 우하단 자리를 쓴다 —
 * 봇은 레벨이 없어 **그 슬롯이 비어 있다**. 새 장식을 더하는 게 아니라 빈 자리를 채우는 것이다.
 * (당초 "링 유무가 두 번째 단서"라 적었으나 2026-09-11 A′로 유저도 전설 외엔 링이 없어져 성립하지 않는다 —
 *  봇/유저 구분은 우하단 도트의 **내용(AI ↔ 레벨 숫자)과 색(accent ↔ 메탈)**이 맡는다.)
 *
 * design.md §G 준수: 테두리 없음(§G-2) · 색은 기존 accent 재사용, 신규 토큰 0(§G-1) · 그림자 없음(§G-3).
 *
 * ⚠ `alignSelf: 'flex-start'` 필수 — 부모가 `display:flex`(댓글 행)면 기본 `align-items:stretch`로
 * 컨테이너가 본문 높이만큼 늘어나고, `position:absolute` 도트의 `bottom` 기준이 함께 내려가
 * **도트가 아바타에서 떨어져 본문 아래에 찍힌다**(2026-09-09 봇 댓글에서 발견 — 본문이 길수록 심하다).
 *
 * ⚠ 봇 여부는 이 컴포넌트를 쓰는 쪽이 서버 `author_is_bot`으로 판정한다. 캐릭터 얼굴은 근거가 아니다
 * (유저도 같은 캐릭터를 무료 아바타·가챠 카드로 장착할 수 있다).
 */
export function BotAuthorAvatar({ persona, author, size = 36 }: {
  persona?: string | null; author?: string | null; size?: number;
}) {
  const slug = botCastId(persona, author);
  // LevelDot과 같은 산식 — 봇은 레벨이 없어 그 자리가 비어 있다. 위치·크기가 어긋나면 두 도트가
  // 다른 종류의 표식으로 보이므로 계산을 일치시킨다. "AI"는 두 글자라 숫자보다 1.5px 줄인다.
  const d = Math.min(40, Math.round(size * 0.44));
  const f = Math.max(7.5, Math.min(16, Math.round(d * 0.5 * 10) / 10) - 1.5);
  const off = -Math.round(d * 0.25);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0, alignSelf: 'flex-start' }}>
      <span
        style={{
          width: size, height: size, borderRadius: '50%', background: 'var(--cs-surface)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, overflow: 'hidden',
        }}>
        <CastAvatar id={slug} expr="focused" size={size - 6} />
      </span>
      {/* AI 도트 — 색은 accent(잉크그린) 고정.
          🔴 gold를 쓰면 안 된다: `--cs-metal-gold`가 `var(--cs-gold)`라 **legend 레벨 도트와 같은 색**이 된다.
          accent는 메탈 5종(iron·steel·bronze·silver·gold) 어느 것과도 겹치지 않는다.
          🔴 2026-09-12: 종전엔 `aria-hidden`이었고 "AI 봇" 라벨은 role 없는 span에 붙어 **무시됐다** —
          닉네임 옆 AI 배지를 이 도트로 옮기면서 스크린리더에선 봇/유저가 구분되지 않게 된 회귀(리뷰 HIGH).
          숨기지 않으면 캐릭터 img alt("황둥이") 다음에 "AI"로 읽힌다. */}
      <span style={{
        position: 'absolute', right: off, bottom: off, width: d, height: d, borderRadius: '50%',
        backgroundColor: 'var(--cs-accent)', backgroundImage: METAL_SHEEN,
        boxShadow: '0 0 0 2px var(--cs-surface)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--cs-surface)', fontWeight: 800, fontSize: f, lineHeight: 1,
      }}>AI</span>
    </span>
  );
}

/** 해금 프레임 키 → 사람이 읽는 이름.
 *  🔴 2026-09-09: 종전엔 `collection_3`·`6`·`9`·`11`과 모든 세트 프레임이 화면에서
 *  똑같이 "컬렉션 프레임" 한 문구로만 표시돼, **무엇을 달성해 받은 것인지 구분되지 않았다**. */
export function frameLabel(key?: string | null): string {
  if (!key) return '';
  if (key.startsWith('set_')) {
    const slug = key.slice(4);
    return `${CAST[slug]?.ko || slug} 세트 프레임`;
  }
  const m = /^collection_(\d+)$/.exec(key);
  return m ? `컬렉션 ${m[1]}종 프레임` : '컬렉션 프레임';
}

const METAL_BG: Record<Metal, string> = {
  iron: 'var(--cs-metal-iron)', steel: 'var(--cs-metal-steel)', bronze: 'var(--cs-metal-bronze)',
  silver: 'var(--cs-metal-silver)', gold: 'var(--cs-metal-gold)', legend: 'var(--cs-metal-gold)',
};
const HEX_CLIP = 'polygon(50% 0,94% 25%,94% 75%,50% 100%,6% 75%,6% 25%)';
// 금속 하이라이트 — 흰색 알파는 명도 파생이라 신규 색이 아니다(§G-1). "회색 점"이 아니라 "쇠 단추"로 보이게 하는 1줄.
const METAL_SHEEN = 'linear-gradient(rgba(255,255,255,.28), rgba(255,255,255,0) 55%)';

/**
 * 레벨 메탈 도트 — 아바타 우하단. 결정(2026-09-03 효신님): ①+③ 혼합안.
 *   avatarSize ≥ 30 → 도트 안에 레벨 숫자 / 그 아래 → 숫자 없이 도트만 폴백(2026-09-09에 32→30).
 *   실루엣: iron·steel = 원, bronze 이상 = 육각 (색만으로 티어 전달 금지 룰 — 실루엣+숫자 병행).
 *   완전 정적. LEGEND 발광은 glow=true(프로필 시트)에서만, 그마저 단색 gold(회전 금지·Q4 레인 분리).
 *   surface 색 갭 2px이 가챠 오라와 금속이 맞닿아 색이 섞이는 걸 막는다.
 */
export function LevelDot({ level, avatarSize, glow = false }: { level: number; avatarSize: number; glow?: boolean }) {
  const metal = metalOf(level);
  const hex = metal !== 'iron' && metal !== 'steel';
  // 🔴 2026-09-09: 종전 임계가 32라 **리더보드가 쓰는 30px에서 숫자가 사라졌다**.
  //   메탈은 2티어가 공유하므로(SCOUT·ANALYST=steel) 색만으로는 Lv6/Lv11, Lv37/Lv45가 구분되지 않는다
  //   (외부 감사 프로브: 30px 렌더 HTML이 동일). 숫자가 유일한 구분자라 임계를 30으로 낮춘다.
  const showNum = avatarSize >= 30;
  // 실측 기준(브리프 §6·시안 실측): 28→9 · 36→16/17 · 52→22 · 132→40(상한)
  const dot = showNum ? Math.min(40, Math.round(avatarSize * 0.44)) : Math.max(8, Math.round(avatarSize * 0.32));
  const fontSize = Math.min(16, Math.max(8.5, Math.round(dot * 0.5 * 10) / 10));
  const offset = -Math.round(dot * 0.25);
  const ring = glow && metal === 'legend'
    ? '0 0 0 2px var(--cs-surface), 0 0 14px 2px var(--cs-gold)'
    : '0 0 0 2px var(--cs-surface)';
  const label = `레벨 ${level}`;
  // 🔴 2026-09-12 대비 정정(better-interface 리뷰 HIGH): 종전엔 전 구간 흰 글자라 은색 2.19:1 · 금색 3.16 ·
  //   브론즈≈3.45 · 강철 4.02로 **철(Lv1~5)만 4.5:1을 넘었다**(8.5~16px 굵은 숫자는 대형 텍스트가 아니라 4.5:1 필요).
  //   밝은 메탈엔 잉크 글자: 브론즈 5.05 · 은색 7.95 · 금색 5.51 ✅.
  //   강철은 흰 4.02 / 잉크 4.33으로 어느 쪽도 미달이라 토큰을 밝혔다(2026-09-13 효신님 (b)) → 잉크 4.77:1 ✅.
  const numColor = metal === 'iron' ? 'var(--cs-surface)' : 'var(--cs-ink)';
  const num = showNum ? (
    <span style={{ color: numColor, fontWeight: 800, fontSize, lineHeight: 1, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{level}</span>
  ) : null;
  if (!hex) {
    return (
      <span role="img" aria-label={label} style={{ position: 'absolute', right: offset, bottom: offset, width: dot, height: dot, borderRadius: '50%',
        backgroundColor: METAL_BG[metal], backgroundImage: METAL_SHEEN, boxShadow: ring,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{num}</span>
    );
  }
  // 육각은 clip-path가 box-shadow를 잘라내므로 surface 육각 래퍼(+4px)로 갭을 만든다. 발광은 래퍼 바깥에 둘 수 없어 drop-shadow 필터로.
  return (
    <span role="img" aria-label={label} style={{ position: 'absolute', right: offset - 1, bottom: offset - 1, width: dot + 4, height: dot + 4,
      background: 'var(--cs-surface)', clipPath: HEX_CLIP, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      filter: glow && metal === 'legend' ? 'drop-shadow(0 0 6px var(--cs-gold))' : undefined }}>
      <span style={{ width: dot, height: dot, clipPath: HEX_CLIP, backgroundColor: METAL_BG[metal], backgroundImage: METAL_SHEEN,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{num}</span>
    </span>
  );
}

/** 목록용 1겹 정적 오라 + (userLevel 있으면) 레벨 메탈 도트. CosmeticCard의 애니메이션/파티클은 사용하지 않는다. */
export function EquippedCosmeticMini({ cosmetic, size = 34, glow = false }: { cosmetic?: SocialCosmetic; size?: number; glow?: boolean }) {
  const slug = cosmetic?.slug || 'hwangdoongi';
  const grade = cosmetic?.grade || null;
  // A′ (효신님 2026-09-11): 목록 아바타 링은 **전설(L)만** 프리즘으로 남기고 기본·N·R·E는 링 없음.
  //   경위 — 등급별 링(N=surface-2 · R=fall-soft · E=gold-soft)은 36px에서 N이 무료와 픽셀 단위로 같고
  //   R·E는 거의 안 보였다. 원색으로 진하게 한 조정안은 "테두리 색으로는 특별한 느낌이 없다"로 반려,
  //   등급별 착장(외부 감사 1안)은 토비 파일럿에서 "완전 별로"로 반려. 그래서 표현을 늘리는 대신 뺐다 —
  //   거의 모두가 평범한 가운데 확률 3%인 전설만 빛나면, 5종이 전부 떠 있을 때보다 특별함이 커진다
  //   (§G-1이 색 절제의 근거로 든 폰 레스토르프 효과). 등급의 풍부한 연출은 프로필 시트의 큰 카드가 맡는다.
  //   ⚠ surface-2 기본 링도 함께 사라진다 — 효신님이 "테두리가 AI slop 아니냐"고 짚었던 그 링이다.
  const aura = grade === 'L' ? PRISM_GRADIENT : 'transparent';
  const auraGlow = 'none'; // V1(2026-09-03): 등급별 색 glow가 아바타마다 진하기를 다르게 만들던 원인 — 등급은 오라 배경색+링만
  const userLevel = Number(cosmetic?.userLevel) || 0;
  // 미선택 채도 저하(2026-09-03 A안) 폐기 — 2026-09-12. ① "안 골랐음"을 **채도(색)로만** 전달했다(리뷰 HIGH:
  //   색에만 의존한 상태 표시) ② 스펙 §11-1 "코스메틱 없음 = 회색 굴욕 기본값 금지"와 정면 충돌 ③ 원래 목적인
  //   봇 황둥이와의 구분은 AI 도트가 맡는다. 미선택 여부는 라벨로만 남긴다.
  const unset = !!cosmetic?.unset;
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0, alignSelf: 'flex-start' }}>
      {/* role="img" — 종전엔 role 없는 span의 aria-label이라 스크린리더가 무시했다(리뷰 MEDIUM) */}
      <span role="img" aria-label={`${CAST[slug]?.ko || '황둥이'} ${grade ? `${GRADE_META[grade].label} 카드` : unset ? '기본 아바타(미선택)' : '기본 아바타'}`}
        style={{ width: size, height: size, padding: 2, borderRadius: '50%', background: aura, boxShadow: auraGlow, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <span style={{ borderRadius: '50%', background: 'var(--cs-surface)', padding: 1, display: 'inline-flex' }}>
          <CastAvatar id={slug} size={size - 6} />
        </span>
      </span>
      {userLevel > 0 && <LevelDot level={userLevel} avatarSize={size} glow={glow} />}
    </span>
  );
}

export function CosmeticIdentityTrigger({
  name,
  cosmetic,
  fontSize = 12.5,
  fontWeight = 600,
  showMini = true,
}: {
  name: string;
  cosmetic?: SocialCosmetic;
  fontSize?: number;
  fontWeight?: number;
  showMini?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" aria-haspopup="dialog" aria-expanded={open}
        onClick={(event) => { event.stopPropagation(); setOpen(true); }}
        style={{ border: 0, padding: 0, background: 'transparent', color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', minWidth: 0 }}>
        {showMini && <EquippedCosmeticMini cosmetic={cosmetic} size={28} />}
        <span style={{ fontSize, fontWeight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
      </button>
      <ProfileSheet open={open} onClose={() => setOpen(false)} name={name} cosmetic={cosmetic} />
    </>
  );
}

/**
 * 작성자 프로필 시트 — 장착 카드(등급 연출 포함)·레벨·해금 보상.
 * 2026-09-11 분리: 종전엔 닉네임 버튼(`CosmeticIdentityTrigger`) 안에 묶여 있어 **닉네임을 눌러야만** 열렸다.
 * A′로 목록 아바타에서 등급 링을 대부분 뺀 대신, 등급의 풍부한 연출(큰 카드 홀로·프리즘)을 보여줄
 * 이 시트로 가는 길을 넓힌다 — 아바타에서도 열리게(`AvatarProfileButton`).
 */
export function ProfileSheet({ open, onClose, name, cosmetic }: {
  open: boolean; onClose: () => void; name: string; cosmetic?: SocialCosmetic;
}) {
  const slug = cosmetic?.slug || 'hwangdoongi';
  const grade = cosmetic?.grade || null;
  const level = Number(cosmetic?.level) || 0;
  const userLevel = Number(cosmetic?.userLevel) || 0;
  const castName = CAST[slug]?.ko || '황둥이';
  return (
  <CSModal open={open} onClose={onClose} title={`${name}의 프로필`} sheet>
      <div style={{ padding: '4px 20px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        {grade ? (
          <div aria-label={cosmetic?.profileFrame ? `${frameLabel(cosmetic.profileFrame)} 적용` : undefined} style={{
            padding: cosmetic?.profileFrame ? 6 : 0,
            borderRadius: 17,
            background: cosmetic?.profileFrame?.startsWith('set_') ? PRISM_GRADIENT : cosmetic?.profileFrame ? 'var(--cs-gold-soft)' : 'transparent',
            boxShadow: cosmetic?.profileFrame ? 'var(--cs-shadow-3)' : 'none',
          }}>
            <CosmeticCard slug={slug} grade={grade} level={level} seedId={cosmetic?.cardId || cosmetic?.cardKey || `${name}:${slug}`} width={190} idle tilt={false} />
          </div>
        ) : (
          /* 🔴 2026-09-09: 프레임 래퍼가 `grade`(가챠 카드) 분기에만 있어서, **기본 아바타를 장착하면
             해금한 컬렉션 프레임이 그려지지 않았다**. 그런데 아래 배지는 `profileFrame`만 보고 떠서
             "프레임 있음"이라 말하면서 화면엔 없는 불일치가 생겼다. 프레임은 카드 보유와 무관한
             달성 보상이므로 기본 아바타에도 적용한다. */
          <div aria-label={cosmetic?.profileFrame ? `${frameLabel(cosmetic.profileFrame)} 적용` : undefined} style={{
            padding: cosmetic?.profileFrame ? 6 : 0,
            borderRadius: '50%',
            background: cosmetic?.profileFrame?.startsWith('set_') ? PRISM_GRADIENT : cosmetic?.profileFrame ? 'var(--cs-gold-soft)' : 'transparent',
            display: 'inline-flex',
          }}>
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              <div style={{ width: 154, height: 154, borderRadius: '50%', background: 'var(--cs-surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CastAvatar id={slug} size={132} />
              </div>
              {/* xl 도트 — 프로필 시트는 LEGEND 단색 gold 발광이 허용되는 유일한 자리(Q4) */}
              {userLevel > 0 && <LevelDot level={userLevel} avatarSize={132} glow />}
            </div>
          </div>
        )}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{castName}</div>
          <div style={{ marginTop: 7, display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
            <CSBadge tone={grade === 'L' ? 'alert' : grade === 'E' ? 'gold' : grade === 'R' ? 'fall' : 'neutral'}>
              {/* 강화는 2026-08-26부터 꺼져 있다(site_settings.enhance_enabled=false). 종전 "N 등급 · 강화 +0"은
                  **없는 기능을 암시**했다(효신님 2026-09-12 지적) → 등급 이름만 사람 말로. */}
              {grade ? `${GRADE_META[grade].label} 카드` : '기본 아바타'}
            </CSBadge>
            {/* 황소 등급 라벨(알등이·전설의 황소…) 제거 — 효신님 2026-09-09.
                ① '전설'이 레벨·카드 L등급·안목 세 곳에서 다른 뜻으로 쓰이던 충돌 중 하나가 사라진다.
                ② 프로필 대표 이미지를 가챠 캐릭터가 맡으므로 황소 진화 표현과 역할이 겹친다.
                ⚠ TIERS 구간과 metalOf(도트 색)는 유지한다 — 라벨만 뺀 것이지 레벨 체계는 그대로다.
                   (황소 진화 이미지 `bullKeyOf`는 실사용처가 없는 죽은 코드로 별도 정리 대상) */}
            {userLevel > 0 && <CSBadge tone="neutral">{`Lv.${userLevel}`}</CSBadge>}
            {cosmetic?.titleKey && <CSBadge tone="ink">세트 칭호 해금</CSBadge>}
            {cosmetic?.profileFrame && <CSBadge tone="neutral">{frameLabel(cosmetic.profileFrame)}</CSBadge>}
          </div>
        </div>
      </div>
    </CSModal>
  );
}

/**
 * 탭하면 프로필 시트가 열리는 목록 아바타 (2026-09-11 효신님: 프로필 시트 동선 보강).
 * ⚠ `stopPropagation` 필수 — 피드 카드 전체가 "글 상세로 이동" 버튼이라, 막지 않으면 아바타 탭이 글로 넘어간다.
 * 봇에는 쓰지 않는다(봇은 프로필 시트가 없고 `BotAuthorAvatar`를 쓴다).
 */
export function AvatarProfileButton({ name, cosmetic, size = 36 }: {
  name: string; cosmetic?: SocialCosmetic; size?: number;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" aria-haspopup="dialog" aria-expanded={open} aria-label={`${name}의 프로필 보기`}
        onClick={(event) => { event.stopPropagation(); setOpen(true); }}
        style={{ border: 0, padding: 0, background: 'transparent', cursor: 'pointer', display: 'inline-flex', flexShrink: 0, alignSelf: 'flex-start' }}>
        <EquippedCosmeticMini cosmetic={cosmetic} size={size} />
      </button>
      <ProfileSheet open={open} onClose={() => setOpen(false)} name={name} cosmetic={cosmetic} />
    </>
  );
}
